import React from 'react';
import { Box, Stack, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../../i18n/constants/keys';

export interface IHighlights {
  data: string[];
}

export const Highlights: React.FC<IHighlights> = ({ data }) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  const accentColors = [
    '#3b82f6',
    '#a855f7',
    '#22c55e',
    '#f97316',
    '#14b8a6',
    '#f43f5e',
  ];

  if (!data || data.length === 0) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ py: 8, px: 2 }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Sparkles size={40} style={{ color: '#9ca3af' }} />
        </Box>
        <Typography variant='h6' sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
          {t(SI.results.userDetails.highlights.noHighlights)}
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 400 }}>
          {t(SI.results.userDetails.highlights.noHighlightsDesc)}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {data.map((item, index) => {
        const accent = accentColors[index % accentColors.length];

        return (
          <Paper
            key={index}
            variant='outlined'
            sx={{
              position: 'relative',
              p: 2.5,
              borderRadius: 2,
              borderColor: 'divider',
              transition: 'all 0.2s',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              '&:hover': {
                boxShadow: 2,
                borderColor: 'grey.300',
              },
            }}
          >
            {/* Left accent bar */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: accent,
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pl: 1 }}>
              {/* Icon */}
              <Box
                sx={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${accent}15`,
                }}
              >
                <CheckCircle2 size={20} style={{ color: accent }} />
              </Box>

              {/* Text content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                  {item}
                </Typography>
              </Box>

              {/* Number badge */}
              <Box
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: `${accent}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant='caption' sx={{ fontWeight: 700, color: accent }}>
                  {index + 1}
                </Typography>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default Highlights;
